import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import {
  buildUpdateSavedViewPayload,
  createSavedView,
  deleteSavedView,
  listSavedViews,
  SavedViewApiError,
  updateSavedView
} from 'app/services/savedViewsApi';
import { savedViewsActions } from './index';

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof SavedViewApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function* loadSavedViewsSaga() {
  try {
    const savedViews: Awaited<ReturnType<typeof listSavedViews>> = yield call(
      listSavedViews
    );
    yield put(savedViewsActions.loadSavedViewsSuccess(savedViews));
  } catch (error) {
    yield put(
      savedViewsActions.loadSavedViewsFailure(
        getErrorMessage(error, 'Failed to load saved views')
      )
    );
  }
}

function* saveViewSaga(action: ReturnType<typeof savedViewsActions.saveView>) {
  try {
    const savedView: Awaited<ReturnType<typeof createSavedView>> = yield call(
      createSavedView,
      action.payload
    );
    yield put(savedViewsActions.saveViewSuccess(savedView));
  } catch (error) {
    yield put(
      savedViewsActions.saveViewFailure(
        getErrorMessage(error, 'Failed to save view')
      )
    );
  }
}

function* updateViewSaga(
  action: ReturnType<typeof savedViewsActions.updateView>
) {
  try {
    const { id, name, description, originalName, originalDescription } =
      action.payload;

    const body = buildUpdateSavedViewPayload(
      { name: originalName, description: originalDescription },
      { name, description }
    );

    const savedView: Awaited<ReturnType<typeof updateSavedView>> = yield call(
      updateSavedView,
      id,
      body
    );
    yield put(savedViewsActions.updateViewSuccess(savedView));
  } catch (error) {
    yield put(
      savedViewsActions.updateViewFailure(
        getErrorMessage(error, 'Failed to update view')
      )
    );
  }
}

function* deleteViewSaga(
  action: ReturnType<typeof savedViewsActions.deleteView>
) {
  try {
    yield call(deleteSavedView, action.payload.id);
    yield put(savedViewsActions.deleteViewSuccess(action.payload.id));
  } catch (error) {
    yield put(
      savedViewsActions.deleteViewFailure(
        getErrorMessage(error, 'Failed to delete view')
      )
    );
  }
}

export function* savedViewsSaga() {
  yield takeLatest(savedViewsActions.loadSavedViews.type, loadSavedViewsSaga);
  yield takeLatest(savedViewsActions.saveView.type, saveViewSaga);
  yield takeLatest(savedViewsActions.updateView.type, updateViewSaga);
  yield takeEvery(savedViewsActions.deleteView.type, deleteViewSaga);
}
